import { priceTypingProcess, priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT } from '../../supplementary_components/requests';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { dateFormatter } from '../../supplementary_components/supplementaryMethods';
import { stripTime } from './expenses';
import { dateToLocalDateString } from './weeklyReports';
import { addNotification } from '../../supplementary_components/store';

export const TRUCK_FIELD = {
    label: 'Truck',
    name: 'truck',
    suggestionsEnabled: true,
    suggestionsLink: API_ROOT + 'trucks/allTrucksBySimilarTruckNumber?pattern=',
    dataValueRequired: true,
    autoComplete: 'off',
    displayValueGenerator: (rawValue) => `${rawValue.truckNumber}`,
    rawValComparationFieldName: 'truckNumber',
    afterMinimumChars: 2,
};

/** @param {boolean} [crossCompany=true] — invoices allow sister-company trailers; Parking money codes do not. */
export function trailerSuggestionsLink(crossCompany = true) {
    return (
        API_ROOT +
        `trailers/trailersBySimilarTrailerNumberForCurrentCompanyAndThirdPartyTrailers?crossCompany=${!!crossCompany}&pattern=`
    );
}

export const TRAILER_FIELD = {
    label: 'Trailer',
    name: 'trailer',
    suggestionsEnabled: true,
    suggestionsLink: trailerSuggestionsLink(true),
    dataValueRequired: true,
    autoComplete: 'off',
    displayValueGenerator: (rawValue) => `${rawValue.trailerNumber}`,
    rawValComparationFieldName: 'trailerNumber',
    afterMinimumChars: 2,
};

export function applyEquipmentTypeFieldVisibility(
    formTarget,
    equipmentType,
    { truckField = 'truck', trailerField = 'trailer' } = {},
) {
    setFormFieldVisibility(formTarget, truckField, equipmentType === 'Truck');
    setFormFieldVisibility(formTarget, trailerField, equipmentType === 'Trailer');
}

export function equipmentTypeOnChange(event, fieldNames = {}) {
    let formTarget = event.target;
    while (formTarget && formTarget.tagName !== 'FORM') {
        formTarget = formTarget.parentNode;
    }
    if (!formTarget) return;
    applyEquipmentTypeFieldVisibility(formTarget, event.target.value, fieldNames);
}

export function invoiceEquipmentTypeOnChange(event) {
    equipmentTypeOnChange(event, { truckField: 'invoiceTruck', trailerField: 'invoiceTrailer' });
}

export function setFormFieldVisibility(formTarget, fieldName, show, clearWhenHidden = true) {
    const label = formTarget.querySelector(`#${fieldName}Label`);
    const fieldWrapper = label?.parentElement;
    if (fieldWrapper) {
        fieldWrapper.style.display = show ? 'block' : 'none';
    }

    const inputFields = formTarget.querySelectorAll(
        `input[name="${fieldName}"], select[name="${fieldName}"], textarea[name="${fieldName}"]`,
    );
    inputFields.forEach((inputField) => {
        inputField.disabled = !show;
        if (!show && clearWhenHidden) {
            if (inputField.tagName === 'SELECT') {
                inputField.value = '';
            } else {
                inputField.value = '';
            }
            inputField.setAttribute('data-value', '');
        }
    });
}

export function buildInvoicePayload(data) {
    const payload = {
        equipmentType: data.equipmentType,
        amount: data.amount,
        createdDate: data.createdDate,
        shopName: data.shopName != null ? String(data.shopName).trim() : '',
        dueDate: data.dueDate,
        paidBy: data.paidBy || null,
        description: data.description || null,
    };
    if (data.status) {
        payload.status = data.status;
    }
    if (data.equipmentType === 'Truck') {
        payload.truck = data.truck;
        payload.trailer = null;
    } else {
        payload.trailer = data.trailer;
        payload.truck = null;
    }
    return payload;
}

export async function validateInvoiceEquipment(data) {
    if (!data.equipmentType) {
        addNotification({ message: 'Select equipment type', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (data.equipmentType === 'Truck' && !data.truck) {
        addNotification({ message: 'Select a truck', type: 'alert' });
        return { cancelSubmission: true };
    }
    if (data.equipmentType === 'Trailer' && !data.trailer) {
        addNotification({ message: 'Select a trailer', type: 'alert' });
        return { cancelSubmission: true };
    }
    return {};
}

export function todayUsDate() {
    return localDateToUSFormat(dateToLocalDateString(stripTime(new Date())));
}

export function buildPayableInvoiceAmountField({ invoice, hiddenInitially = false } = {}) {
    return {
        label: 'Amount',
        name: 'amount',
        mandatory: true,
        placeholder: '$0.00',
        autoComplete: 'off',
        customDisplayMode: hiddenInitially ? 'none' : undefined,
        disabled: hiddenInitially,
        filters: [priceTypingProcess],
        onblur: (val) => priceOnFinish(val),
        defaultValue: () => (invoice?.amount != null ? priceOnFinish(invoice.amount) : ''),
        processBeforeSending: (val) => parseFloat(String(val).replace(/[^0-9.]/g, '')),
    };
}

export function buildPayableInvoiceDateFields({ invoice, hiddenInitially = false } = {}) {
    const todayUs = todayUsDate();
    return [
        {
            label: 'Date',
            name: 'createdDate',
            mandatory: true,
            autoComplete: 'off',
            calendarPickType: 'singleDate',
            customDisplayMode: hiddenInitially ? 'none' : undefined,
            disabled: hiddenInitially,
            defaultValue: () =>
                invoice?.createdDate ? localDateToUSFormat(invoice.createdDate) : todayUs,
            onblur: (val) => dateFormatter(val)['date'] || val,
            checks: [
                (value) => {
                    if (!dateFormatter(value)['date']) return 'Invalid Date';
                    return null;
                },
            ],
            processBeforeSending: (val) => dateToLocalDateString(new Date(val)),
        },
        {
            label: 'Due Date',
            name: 'dueDate',
            autoComplete: 'off',
            calendarPickType: 'singleDate',
            customDisplayMode: hiddenInitially ? 'none' : undefined,
            disabled: hiddenInitially,
            defaultValue: () => (invoice?.dueDate ? localDateToUSFormat(invoice.dueDate) : ''),
            onblur: (val) => (val ? dateFormatter(val)['date'] || val : ''),
            checks: [
                (value) => {
                    if (!value || !String(value).trim()) return null;
                    if (!dateFormatter(value)['date']) return 'Invalid Date';
                    return null;
                },
            ],
            processBeforeSending: (val) => {
                if (!val || !String(val).trim()) return null;
                return dateToLocalDateString(new Date(val));
            },
        },
    ];
}

export const PAYABLE_INVOICE_PAID_BY_OPTIONS = [
    { value: '', label: '' },
    { value: 'MoneyCode', label: 'Money Code' },
    { value: 'Check', label: 'Check' },
    { value: 'Card', label: 'Card' },
    { value: 'Ach', label: 'ACH' },
];

export function formatPaidBy(paidBy) {
    if (!paidBy) return '-';
    if (paidBy === 'MoneyCode') return 'Money Code';
    if (paidBy === 'Ach') return 'ACH';
    return String(paidBy);
}

export function buildPayableInvoicePaidByField({ invoice, hiddenInitially = false } = {}) {
    return {
        label: 'Paid By',
        name: 'paidBy',
        type: 'select',
        dataset: PAYABLE_INVOICE_PAID_BY_OPTIONS,
        customDisplayMode: hiddenInitially ? 'none' : undefined,
        disabled: hiddenInitially,
        defaultValue: () => invoice?.paidBy ?? '',
        processBeforeSending: (val) => (val ? val : null),
    };
}

export function buildPayableInvoiceShopInfoField({ invoice, hiddenInitially = false } = {}) {
    return {
        label: 'Shop Info',
        name: 'shopName',
        autoComplete: 'off',
        suggestionsEnabled: true,
        suggestionsLink: API_ROOT + 'shops?pattern=',
        displayValueGenerator: (shop) => shop?.name ?? '',
        suggestOnEachKey: true,
        noValidation: true,
        customDisplayMode: hiddenInitially ? 'none' : undefined,
        disabled: hiddenInitially,
        defaultValue: () => invoice?.shopName ?? '',
        processBeforeSending: (val) => (val != null ? String(val).trim() : ''),
    };
}

export function buildNewPayableInvoiceFormColumns({ hiddenInitially = true } = {}) {
    const [createdDateField, dueDateField] = buildPayableInvoiceDateFields({ hiddenInitially });
    return [
        {
            label: 'Equipment type',
            name: 'equipmentType',
            type: 'select',
            dataset: ['', 'Truck', 'Trailer'],
            mandatory: true,
            customDisplayMode: hiddenInitially ? 'none' : undefined,
            disabled: hiddenInitially,
            onChange: equipmentTypeOnChange,
        },
        {
            ...TRUCK_FIELD,
            mandatory: true,
            customDisplayMode: 'none',
            disabled: true,
        },
        {
            ...TRAILER_FIELD,
            mandatory: true,
            customDisplayMode: 'none',
            disabled: true,
        },
        buildPayableInvoiceAmountField({ hiddenInitially }),
        createdDateField,
        buildPayableInvoiceShopInfoField({ hiddenInitially }),
        dueDateField,
        {
            label: 'Description',
            name: 'description',
            type: 'textArea',
            rowCount: 4,
            customDisplayMode: hiddenInitially ? 'none' : undefined,
            disabled: hiddenInitially,
            defaultValue: () => '',
        },
    ];
}
